import React from 'react';
import moment from 'moment';
import i18n from 'i18n';
import {
  SpaceHealthState,
  SPACE_HEALTH_STATUS_I18N
} from 'enum/SpaceHealthState';
import styles from './spaceHealthTooltip.module.scss';

type SpaceHealthInfo = {
  state?: SpaceHealthState;
  spaceName?: string;
  /** Reason text from the backend; only used for `banned`. */
  reason?: string;
  /** ISO timestamp of the latest status sync. */
  lastSyncedAt?: string;
  deleted?: boolean;
};

type SpaceHealthTooltipProps = {
  state: SpaceHealthState;
  spaceName?: string;
  reason?: string;
  lastSyncedAt?: string;
  deleted?: boolean;
};

// Fixed copy per the spec; `banned` instead shows the backend-provided reason.
const FIXED_CONTENT_I18N: Partial<Record<SpaceHealthState, string>> = {
  [SpaceHealthState.WARMING_UP]: 'spaceHealth.descriptions.warmingUp',
  [SpaceHealthState.NO_DATA]: 'spaceHealth.descriptions.noData'
};

export const SpaceHealthTooltip: React.FC<SpaceHealthTooltipProps> = ({
  state,
  spaceName,
  reason,
  lastSyncedAt,
  deleted
}) => {
  // 已刪除時運作狀態的說明（停止服務原因、訊號異常…）都不再適用。
  const content = deleted
    ? undefined
    : state === SpaceHealthState.BANNED
      ? reason
      : FIXED_CONTENT_I18N[state]
        ? i18n.t(FIXED_CONTENT_I18N[state] as string)
        : undefined;
  const footer = lastSyncedAt
    ? i18n.t('spaceHealth.labels.lastUpdated', {
        time: moment(lastSyncedAt).format('MM/DD HH:mm')
      })
    : undefined;

  return (
    <div className={styles.spaceHealthTooltip}>
      <div className={styles.header}>
        <span
          className={`${styles.dot} ${deleted ? styles.deleted : styles[state]}`}
        />
        {deleted
          ? i18n.t('spaceHealth.labels.deleted')
          : i18n.t(SPACE_HEALTH_STATUS_I18N[state])}
      </div>
      {spaceName && <div className={styles.spaceName}>{spaceName}</div>}
      {content && (
        <>
          <div className={styles.divider} />
          <div className={styles.content}>{content}</div>
        </>
      )}
      {footer && (
        <>
          <div className={styles.divider} />
          <div className={styles.footer}>{footer}</div>
        </>
      )}
    </div>
  );
};

/**
 * Render the operational-status tooltip for a chip (normal or abnormal), or
 * `undefined` when the chip has no resolved state so the caller can fall back
 * to its default tooltip.
 */
export const renderSpaceHealthTooltip = (
  health: SpaceHealthInfo
): JSX.Element | undefined => {
  if (!health.state) return undefined;
  return (
    <SpaceHealthTooltip
      state={health.state}
      spaceName={health.spaceName}
      reason={health.reason}
      lastSyncedAt={health.lastSyncedAt}
      deleted={health.deleted}
    />
  );
};
